import { useQuery } from '@tanstack/react-query';

import { withGlobalError } from '@/react-tools/react-query';
import axios, { parseAxiosError } from '@/portainer/services/axios/axios';

import { queryKeys, PolicyConflictsRequest } from './query-keys';

// Re-export for consumers
export type { PolicyConflictsRequest } from './query-keys';

/**
 * Represents an environment group that has an existing policy of the same type
 * which will be replaced/overridden by the new policy.
 */
export interface ConflictInfo {
  /** Unique ID of the environment group */
  environmentGroupId: number;
  /** Display name of the environment group */
  environmentGroupName: string;
  /** ID of the existing policy that will be replaced */
  existingPolicyId: number;
  /** Name of the existing policy that will be replaced */
  existingPolicyName: string;
  /** Total number of environments in this group */
  environmentCount: number;
  /** Number of supported environments (compatible with this policy type) */
  supportedEnvironments: number;
  /** Number of unsupported environments (NOT compatible with this policy type) */
  unsupportedEnvironments: number;
}

/**
 * Represents an environment group that will newly receive the policy
 * (has no existing policy of the same type).
 */
export interface NewGroupInfo {
  /** Unique ID of the environment group */
  environmentGroupId: number;
  /** Display name of the environment group */
  environmentGroupName: string;
  /** Total number of environments in this group */
  environmentCount: number;
  /** Number of environments compatible with this policy type */
  supportedEnvironments: number;
  /** Number of environments NOT compatible with this policy type */
  unsupportedEnvironments: number;
}

/**
 * Represents an environment group that is being removed from the policy
 * (was previously assigned but is no longer selected).
 */
export interface DetachedGroupInfo {
  /** Unique ID of the environment group */
  environmentGroupId: number;
  /** Display name of the environment group */
  environmentGroupName: string;
  /** Number of environments in this group */
  environmentCount: number;
}

/**
 * Response from the policy conflicts API endpoint.
 */
export interface PolicyConflictsResponse {
  /** Groups with existing policies that will be replaced */
  conflicts: Array<ConflictInfo>;
  /** Groups that will newly receive this policy */
  newGroups: Array<NewGroupInfo>;
  /** Total number of environments across all groups */
  totalEnvironments: number;
  /** Total number of environments compatible with this policy type */
  supportedEnvironments: number;
  /** Total number of environments NOT compatible with this policy type */
  unsupportedEnvironments: number;
}

/**
 * React Query hook for fetching policy conflicts.
 * Automatically handles loading, error, and caching states.
 *
 * @param request - The conflict check request parameters
 * @param options - Optional configuration (enabled)
 */
export function usePolicyConflicts(
  request: PolicyConflictsRequest,
  { enabled = true }: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: queryKeys.conflicts(request),
    queryFn: () => getPolicyConflicts(request),
    enabled: enabled && request.environmentGroups.length > 0,
    ...withGlobalError('Failed to load policy preview'),
  });
}

async function getPolicyConflicts(
  request: PolicyConflictsRequest
): Promise<PolicyConflictsResponse> {
  try {
    const { data } = await axios.post<PolicyConflictsResponse>(
      '/policies/conflicts',
      request
    );
    return data;
  } catch (e) {
    throw parseAxiosError(e, 'Failed to fetch policy conflicts');
  }
}
